import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider, 
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Breadcrumbs,
  Link,
  Tooltip,
  IconButton,
  Fade,
  Alert,
  Stack
} from "@mui/material";
import Header from "../../components/Header";
import { getUserInfo } from "../../api/auth";
import { useNavigate } from "react-router-dom";

// アイコン
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GoogleIcon from '@mui/icons-material/Google';
import ScienceIcon from '@mui/icons-material/Science';

const SettingsAccount = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const userData = await getUserInfo();
        setUser(userData);
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress color="primary" />
        </Container>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            ユーザー情報の取得に失敗しました。再度お試しいただくか、管理者にお問い合わせください。
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/setting-Home')}
            sx={{ mt: 2 }}
          >
            設定ホームに戻る
          </Button>
        </Container>
      </>
    );
  }

  const { username, email, plan } = user;

  return (
    <>
      <Header username={username} />
      <Container 
  maxWidth="md" 
  sx={{ 
    mt: 4, 
    mb: 6, 
    px: { xs: 2, sm: 3 },
    // PWAボトムナビゲーション対応：安全マージンを確保
    pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
    // 最小高さを確保してスクロール可能にする
    minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
  }}
>
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={() => navigate('/setting-Home')}
            style={{ cursor: 'pointer' }}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <AccountCircleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            アカウント情報
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                アカウント情報
              </Typography>
              <Typography color="text.secondary">
                ログイン情報と基本的なアカウント設定を管理します
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {/* プロフィールカード */}
                <Card 
                  elevation={2} 
                  sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: '100%'
                  }}
                >
                  <Box 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      py: 3,
                      px: 2,
                      textAlign: 'center',
                      color: 'white'
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mx: 'auto', 
                        mb: 2,
                        bgcolor: 'white',
                        color: 'primary.main',
                        fontSize: '2rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {username ? username.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Typography variant="h6" fontWeight="medium">
                      {username}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {plan?.name || 'フリープラン'}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <List>
                      <ListItem sx={{ px: 3, py: 2 }}>
                        <ListItemIcon>
                          <AccountCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="ユーザー名"
                          secondary={username}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ px: 3, py: 2 }}>
                        <ListItemIcon>
                          <EmailIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="メールアドレス"
                          secondary={email}
                        />
                      </ListItem>
                      <Divider />
                      <ListItem sx={{ px: 3, py: 2 }}>
                        <ListItemIcon>
                          <GoogleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="認証方法"
                          secondary="Google アカウント"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                {/* アカウント詳細情報 */}
                <Stack spacing={3}>
                  {/* セキュリティ情報 */}
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        px: 3, 
                        py: 2, 
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <SecurityIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="medium">
                        セキュリティ情報
                      </Typography>
                    </Box>
                    <Box p={3}>
                      <List disablePadding>
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemText 
                            primary="ログイン方法"
                            secondary="Google認証（シングルサインオン）"
                          />
                          <Tooltip title="安全な認証方法を使用しています">
                            <VerifiedUserIcon color="success" />
                          </Tooltip>
                        </ListItem>
                        <ListItem sx={{ px: 0, py: 1.5 }}>
                          <ListItemText 
                            primary="最終ログイン"
                            secondary="本日"
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Paper>

                  {/* プランと契約情報 */}
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box 
                      sx={{ 
                        px: 3, 
                        py: 2, 
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <VerifiedUserIcon />
                      <Typography variant="subtitle1" fontWeight="medium">
                        プランと契約情報
                      </Typography>
                    </Box>
                    <Box p={3}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            現在のプラン
                          </Typography>
                          <Typography variant="h6" fontWeight="medium" gutterBottom>
                            {plan?.name || 'フリープラン'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            月額料金: ¥{plan?.price?.toLocaleString() || '0'}（税込）
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            利用制限
                          </Typography>
                          <Box sx={{ '& > p': { mb: 0.5 } }}>
                          	<Typography variant="body2">
  製品登録上限: {plan?.name === 'Standard' ? '10 件' : '1 件'}
</Typography>
                            <Typography variant="body2">
                              顧客登録上限: {plan?.name === 'Standard' ? '無制限' : `${plan?.max_clients || 0} 件`}
                            </Typography>
                            <Typography variant="body2">
                              履歴保存上限: {plan?.name === 'Standard' ? '無制限' : `${plan?.max_history || 0} 件/顧客`}
                            </Typography>
                            <Typography variant="body2">
                              グループ数: {plan?.max_groups || 0} 件
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate('/settings/plan')}
                        >
                          プラン設定を変更
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
            
            {/* アクションボタン */}
              <Box sx={{ 
    mt: 4, 
    display: 'flex', 
    justifyContent: 'center',
    // モバイルでは追加の下部余白を確保
    pb: { xs: 2, sm: 0 }
  }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/setting-Home')}
                size="large"
                sx={{ minWidth: 200,
                minHeight: { xs: 48, sm: 40 }
                 }}
              >
                設定ホームに戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>
    </>
  );
};

export default SettingsAccount;